import { fetchAuthSession } from "aws-amplify/auth";

import { API_BASE_URL, IS_DEV } from "../constants";
import {
  assertClinicScopeCurrent,
  clearActiveClinicId,
  getActiveClinicHeader,
} from "../utils/active-clinic";
import { getDeviceId } from "../utils/device-id";
import { reportNetworkBlocked } from "../utils/flow-telemetry";
import { OFFLINE_ERROR_CODE } from "../utils/network";
import { ApiError, ApiErrorDetail, NotAuthenticatedError } from "./api.errors";

const API_TIMEOUT_MS = 30_000;

/**
 * Identity headers every request to our own API must carry.
 *
 * Code that needs the raw Response (sync engines, blob downloads, uploads)
 * uses `apiFetch`, never a bare `fetch` — a request missing these is not just
 * incomplete: without X-Device-Id the device-trust gate 403s every doctor and
 * receptionist, and without X-Active-Clinic a switched owner's request is
 * served, and written, as their HOME clinic. The build rejects a bare
 * fetch(`${API_BASE_URL}…`) (no-restricted-syntax, package.json), and
 * src/api-fetch-guard.test.ts also catches one made through a URL variable.
 *
 * Throws ClinicScopeChangedError when another tab has moved this browser to a
 * different clinic: this tab's header would name the clinic it is still
 * showing, not the one the shared cache now holds.
 *
 * Throws NotAuthenticatedError when there is no session to authenticate with,
 * rather than sending a request only the backend can reject.
 */
export async function buildApiHeaders(): Promise<Record<string, string>> {
  assertClinicScopeCurrent();
  const headers: Record<string, string> = {};

  let token: string | undefined;
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    token = session.tokens?.idToken?.toString();
  } catch {
    // Amplify throws here instead of resolving empty on some failures. Either
    // way there is no token, which the check below handles.
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else if (!IS_DEV) {
    // Refuse to send a request the backend can only answer with 401 "Missing
    // authorization header": that string is backend-internal and no user can
    // act on it, yet it is what reaches the screen when a signed-out session
    // keeps calling. Fail here instead, with copy the caller can show, and
    // never leak the backend string.
    //
    // Local dev is the one exception: it runs the backend with
    // AUTH_ENABLED=False and no Cognito session at all.
    throw new NotAuthenticatedError();
  }

  // The two device check/verify endpoints use get_jwt_user (exempt from trust check).
  headers["X-Device-Id"] = getDeviceId();

  // Multi-clinic owners: name the clinic this request is scoped to. The backend
  // validates it against org membership on EVERY request before feeding it to
  // RLS — this header is a request, never a grant. Sent only when that clinic
  // is not the user's home clinic: without it the backend scopes to home
  // anyway, and a custom header the API gateway's CORS allow-list lacks fails
  // the request outright — so staff must never carry it.
  const activeClinic = getActiveClinicHeader();
  if (activeClinic) headers["X-Active-Clinic"] = activeClinic;

  return headers;
}

/**
 * `fetch` that reports a request no server ever answered (NETWORK_BLOCKED).
 * Behaviour is unchanged: the error or response goes back to the caller as is.
 */
async function fetchReportingBlocked(url: string, init: RequestInit): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    // A TypeError is the browser's only signal for CORS, DNS and TLS failures.
    // A timeout or abort is a DOMException and did reach the network.
    if (err instanceof TypeError) reportNetworkBlocked(url, err);
    throw err;
  }
  // With the service worker installed the TypeError never reaches us: sw.js
  // swallows it and answers with a tagged synthetic 503 (see networkOnly).
  // Plain .get("...") on purpose: check-prod-cors-contract.py proves this
  // header is only ever READ, and it recognises that exact call shape.
  if (!response.ok && response.headers && response.headers.get("X-Kaagazy-Offline") === "1") {
    reportNetworkBlocked(url);
  }
  return response;
}

/** `fetch` for our own API, with the identity headers above. Caller headers win. */
export async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = await buildApiHeaders();
  return fetchReportingBlocked(url, {
    ...init,
    headers: { ...headers, ...(init.headers as Record<string, string> | undefined) },
  });
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = await buildApiHeaders();

  // FormData manages its own Content-Type boundary — don't override it.
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetchReportingBlocked(url, {
    ...options,
    signal: options.signal ?? AbortSignal.timeout(API_TIMEOUT_MS),
    headers: { ...headers, ...(options.headers as Record<string, string>) },
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    let details: ApiErrorDetail = {};
    try {
      const body = await response.json();
      if (body.error?.message) {
        // Standard BaseAPIResponse envelope: { success: false, error: { code, message, details } }
        message = body.error.message;
        if (body.error.details?.field) {
          details = { field: body.error.details.field, code: body.error.code };
        }
      } else if (typeof body.error === "string") {
        // Bare `{ error, upgrade_url, ... }` 402 shape used by the feature-gate
        // (`feature_not_enabled` / `trial_expired`, see requires_feature in
        // feature_flags.py) and the notification-credit gate
        // (`insufficient_credits`, see InsufficientCreditsError). Not wrapped in
        // the standard envelope or in `detail` — surface the code verbatim as
        // the message so callers can branch on `err.message === "insufficient_credits"`.
        message = body.error;
        details = { ...details, code: body.error };
        if (typeof body.upgrade_url === "string") {
          details.upgradeUrl = body.upgrade_url;
        }
      } else if (body.message) {
        message = body.message;
      } else if (Array.isArray(body.detail)) {
        // Pydantic 422 without custom error handler — map each loc+msg pair to a readable string.
        message = body.detail
          .map((e: { loc?: string[]; msg?: string }) => {
            const field = e.loc?.slice(1).join(".") ?? "field";
            return `${e.msg ?? "Validation error"} (${field})`;
          })
          .join("; ");
      } else if (body.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
        // FastAPI HTTPException with dict detail — e.g. { error: "ai_consent_required", message: "..." }
        message = body.detail.message || body.detail.error || "Request failed";
        if (body.detail.error) {
          details = { ...details, code: body.detail.error };
        }
      } else if (body.detail) {
        message = String(body.detail);
      }
    } catch { /* JSON parse failed — keep HTTP status fallback */ }

    // sw.js turns a failed fetch into a synthetic 503 (see networkOnly), which
    // otherwise looks identical to a 503 from the backend. Tag it so callers with
    // an offline fallback can recognise a genuine connectivity failure instead of
    // guessing from the status code.
    if (response.headers.get("X-Kaagazy-Offline") === "1") {
      details = { ...details, code: OFFLINE_ERROR_CODE };
    }
    const err = new ApiError(message, response.status, details);

    // Global interceptors — dispatch events so components outside the call site
    // can react without prop-drilling error state up the tree.
    if (response.status === 403 && message === "device_verification_required") {
      window.dispatchEvent(new CustomEvent("kaagazy:device-verification-required"));
    }
    if (response.status === 401 && message === "session_revoked") {
      window.dispatchEvent(new CustomEvent("kaagazy:session-revoked"));
    }
    if (response.status === 403 && message === "account_disabled") {
      window.dispatchEvent(new CustomEvent("kaagazy:account-disabled"));
    }
    // The stored active clinic is no longer reachable — moved out of the org,
    // or deactivated since it was selected. Drop it immediately so the next
    // request falls back to the home clinic instead of 403ing forever, and let
    // ClinicContext re-resolve and flush the now-wrong cached data.
    //
    // The backend deliberately 403s here rather than silently serving the home
    // clinic: a silent fallback would render clinic A's data under clinic B's
    // name in the switcher, which is worse than an error.
    if (response.status === 403 && message === "active_clinic_invalid") {
      clearActiveClinicId();
      window.dispatchEvent(new CustomEvent("kaagazy:active-clinic-invalid"));
    }

    throw err;
  }

  if (response.status === 204) return null as T;

  const data = await response.json();
  // Unwrap BaseAPIResponse envelope: { success, data, timestamp }
  if (data && typeof data === "object" && "success" in data && "data" in data) {
    return data.data as T;
  }
  return data as T;
}
